import { ScheduleCampaign } from '@/screens/MaterialCreate';
import { useMaterialEditStore } from '@/screens/MaterialEdit/store';
import { campaignDisplayFormatMapper } from '@/utils/mapper';
import { useDeviceDetect } from '@/utils/useMediaQuery';
import { EDisplayFormat } from '@shelflix/schemas/_common/enum';
import { CampaignDto } from '@shelflix/trpc/routes/campaign/list/useCase';
import { DatePicker } from '@shelflix/ui-web/components/DatePicker/index';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@shelflix/ui-web/components/ui/dialog';
import { Label } from '@shelflix/ui-web/components/ui/label';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { AlertModal } from '../AlertModal';

interface ScheduleMaterialProps {
  openModal: boolean;
  campaign: CampaignDto | null;
  scheduleCampaigns: ScheduleCampaign[];
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

export function ScheduleMaterialModal({
  openModal = false,
  campaign,
  onOpenChange,
  scheduleCampaigns,
  children,
}: ScheduleMaterialProps) {
  const { addScheduledCampaign, removeScheduledCampaign } = useMaterialEditStore();
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [conflictingCampaign, setConflitiongCampaign] = useState<ScheduleCampaign | null>(null);
  const formatDate = (date?: string | Date) => (date ? dayjs(date).format('DD/MM/YYYY') : '-');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleSave = () => {
    if (!startDate || !endDate) return;

    const newStart = dayjs(startDate);
    const newEnd = dayjs(endDate);

    const conflicting = scheduleCampaigns.find((scheduled) => {
      if (!scheduled.startDate || !scheduled.endDate) return false;

      const scheduledStart = dayjs(scheduled.startDate);
      const scheduledEnd = dayjs(scheduled.endDate);

      const hasOverlap = newStart.isBefore(scheduledEnd) && newEnd.isAfter(scheduledStart);

      return hasOverlap;
    });

    if (conflicting) {
      setConflitiongCampaign(conflicting);
      setOpenAlertModal(true);
      return;
    }

    addScheduledCampaign(campaign, startDate, endDate);
    onOpenChange(false);
  };

  const confirmSave = () => {
    removeScheduledCampaign(conflictingCampaign?.campaign);
    addScheduledCampaign(campaign, startDate, endDate);
    setOpenAlertModal(false);
    onOpenChange(false);
  };

  useEffect(() => {
    if (openModal === false) {
      setStartDate('');
      setEndDate('');
    }
  }, [openModal]);

  const { isMobile } = useDeviceDetect();

  return (
    <Dialog open={openModal} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] w-full overflow-y-auto border-none p-12 sm:min-w-[563px]"
        style={{
          padding: isMobile ? '48px 24px' : '48px',
        }}
      >
        <DialogHeader className="mb-4 flex flex-col items-center gap-4">
          <DialogClose asChild className="absolute top-4 right-4 cursor-pointer">
            <Icon name="close" size={32} className="cursor-pointer text-red-500" />
          </DialogClose>
          <div className="flex w-full items-center justify-center">
            <Icon
              name="calendar_add_on"
              fill={1}
              size={32}
              className="rounded-md bg-[var(--primary)]/30 p-2 text-[var(--primary)]"
            />
          </div>

          <DialogTitle className="font-semibold">Agendamento de campanha</DialogTitle>
        </DialogHeader>

        {children}

        <div className="bg-foreground flex w-full flex-col rounded-[10px] p-4">
          <div className="flex w-full justify-between">
            <span className="text-xs font-medium">Formato</span>
            <span className="text-xs font-normal">
              {campaignDisplayFormatMapper(campaign?.displayFormat as EDisplayFormat)}
            </span>
          </div>
          <div className="flex w-full justify-between">
            <span className="text-xs font-medium">Marca vinculada</span>
            <span className="text-xs font-normal">{campaign?.brand.name}</span>
          </div>
          <div className="flex w-full justify-between">
            <span className="text-xs font-medium">Limite para veiculação</span>
            <span className="text-xs font-normal">{formatDate(campaign?.endDate)}</span>
          </div>
        </div>

        <div className="flex w-full flex-col">
          <Label className="text-xs text-[var(--input-color)]">Data de Início</Label>
          <div className="flex flex-row gap-2">
            <DatePicker
              mode="single"
              placeholder="dd/mm/aaaa"
              onChange={(e: Date) => {
                setStartDate(e.toISOString());
              }}
              value={startDate ? new Date(startDate) : undefined}
              fromDate={new Date()}
              toDate={campaign?.endDate ? new Date(campaign.endDate) : undefined}
              portalled={!isMobile}
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-1">
          <Label className="text-xs text-[var(--input-color)]">Data de Encerramento</Label>
          <div className="flex flex-row gap-2">
            <DatePicker
              mode="single"
              placeholder="dd/mm/aaaa"
              onChange={(e: Date) => {
                setEndDate(e.toISOString());
              }}
              value={endDate ? new Date(endDate) : undefined}
              fromDate={startDate ? new Date(startDate) : new Date()}
              toDate={campaign?.endDate ? new Date(campaign.endDate) : undefined}
              portalled={!isMobile}
            />
          </div>
        </div>

        <div className="mt-4 flex w-full justify-center">
          <Button onClick={() => handleSave()} className="w-full">
            <Icon name="calendar_add_on" className="mr-2 text-white" fill={1} />
            <span>Agendar campanha</span>
          </Button>
        </div>

        <AlertModal
          openModal={openAlertModal}
          onOpenChange={setOpenAlertModal}
          icon={<Icon name="calendar_add_on" className="text-primary" size={40} fill={1} />}
          title="Alerta importante!"
          subtitle="Campanha em conflito de datas, confirme a sua ação."
          content={
            <div className="text-center font-[Figtree] text-base text-[#DC3545]">
              Atenção, a campanha{' '}
              <strong>
                {conflictingCampaign?.campaign.name} está agendada para data{' '}
                {formatDate(conflictingCampaign?.startDate)} a {formatDate(conflictingCampaign?.endDate)}
              </strong>
              , e está em conflito com a sua seleção atual. <br /> <br />
              Em caso de confirmação, a nova campanha irá se sobrepor à antiga.
            </div>
          }
          actionBotton={
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={confirmSave} size="lg">
                <Icon name="event_list" className="text-primary" fill={1} />
                <span className="text-primary">Adicionar mesmo assim</span>
              </Button>

              <Button onClick={() => setOpenAlertModal(false)} size="lg">
                <Icon name="calendar_apps_script" className="text-white" fill={1} />
                <span className="text-white">Editar data</span>
              </Button>
            </div>
          }
        />
      </DialogContent>
    </Dialog>
  );
}
